import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "../../utils/toast";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { createTestOrder } from "../../store/slices/testOrderSlice";
import { fetchTests, fetchTestCategories } from "../../store/slices/testSlice";
import { fetchQuestionsByTest } from "../../store/slices/dynamicQuestionSlice";
import { createVisit } from "../../store/slices/visitSlice";

const TestOrder = ({ patient, onOrderComplete, onBack }) => {
  const dispatch = useDispatch();
  const {
    tests,
    testCategories,
    loading: testsLoading,
  } = useSelector((state) => state.tests);
  const { loading: orderLoading } = useSelector((state) => state.testOrders);
  const { questionsByTest } = useSelector((state) => state.dynamicQuestions);
  const { user } = useSelector((state) => state.auth || {});

  const currentPatient = patient?.id ? patient : patient?.data ?? patient;

  const [selectedTests, setSelectedTests] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingQuestionsForTest, setLoadingQuestionsForTest] = useState(null);
  const [expandedQuestionsByTestId, setExpandedQuestionsByTestId] = useState({});

  // Fetch tests on component mount
  useEffect(() => {
    dispatch(fetchTests());
    dispatch(fetchTestCategories());
  }, [dispatch]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const categoryChips = [
    { id: "all", name: "All Tests" },
    ...((testCategories || []).map((c) => ({ id: c.id, name: c.name })) || []),
  ];
  // Filter tests based on search and category
  const filteredTests = (tests || []).filter((test) => {
    const matchesSearch =
      test.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || test.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get questions for a specific test
  const getTestQuestions = (testId) => {
    return questionsByTest[testId] || [];
  };

  const handleTestToggle = async (test) => {
    if (selectedTests.find((t) => t.id === test.id)) {
      // Deselect test
      setSelectedTests(selectedTests.filter((t) => t.id !== test.id));
      // Remove dynamic answers for this test
      const newAnswers = { ...dynamicAnswers };
      delete newAnswers[test.id];
      setDynamicAnswers(newAnswers);
    } else {
      // Select test
      setSelectedTests([...selectedTests, test]);
      setExpandedQuestionsByTestId((prev) => ({ ...prev, [test.id]: true }));

      // Fetch dynamic questions for this test if not already loaded
      if (!questionsByTest[test.id]) {
        setLoadingQuestionsForTest(test.id);
        try {
          await dispatch(fetchQuestionsByTest(test.id)).unwrap();
        } catch (error) {
          console.error("Failed to fetch questions for test:", test.id, error);
          toast.error(`Failed to load questions for ${test.name}`);
        } finally {
          setLoadingQuestionsForTest(null);
        }
      }
    }
  };

  const handleDynamicAnswer = (testId, questionId, value) => {
    setDynamicAnswers((prev) => ({
      ...prev,
      [testId]: {
        ...prev[testId],
        [questionId]: value,
      },
    }));
  };

  const asMoneyNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/[^0-9.-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const calculateTotal = () => {
    return selectedTests.reduce((total, test) => total + asMoneyNumber(test.price), 0);
  };

  const renderDynamicQuestion = (testId, question) => {
    const currentAnswer = dynamicAnswers[testId]?.[question.id];

    const label = (
      <span className="text-sm font-semibold text-gray-900">
        {question.question_text}{" "}
        {question.is_required ? <span className="text-red-600">*</span> : null}
      </span>
    );

    const fieldBase =
      "mt-2 w-full rounded-xl bg-white px-3.5 py-3 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2]";

    switch (question.field_type) {
      case "radio":
        return (
          <div key={question.id} className="space-y-2">
            <div>{label}</div>
            <div className="flex flex-wrap gap-2">
              {question.options?.map((option) => (
                <label
                  key={option}
                  className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-2 text-sm text-gray-800 ring-1 ring-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="radio"
                    name={`${testId}-${question.id}`}
                    value={option}
                    checked={currentAnswer === option}
                    onChange={(e) =>
                      handleDynamicAnswer(testId, question.id, e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="h-5 w-5 text-[#36F1A2] focus:ring-[#36F1A2]"
                    required={question.is_required}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case "checkbox":
        return (
          <div key={question.id} className="space-y-2">
            <div>{label}</div>
            <div className="flex flex-wrap gap-2">
              {question.options?.map((option) => (
                <label
                  key={option}
                  className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-2 text-sm text-gray-800 ring-1 ring-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={currentAnswer?.includes?.(option) || false}
                    onChange={(e) => {
                      const currentValues = currentAnswer || [];
                      const newValues = e.target.checked
                        ? [...currentValues, option]
                        : currentValues.filter((v) => v !== option);
                      handleDynamicAnswer(testId, question.id, newValues);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-5 w-5 rounded text-[#36F1A2] focus:ring-[#36F1A2]"
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case "text":
        return (
          <div key={question.id} className="space-y-2">
            <div>{label}</div>
            <input
              type="text"
              value={currentAnswer || ""}
              onChange={(e) =>
                handleDynamicAnswer(testId, question.id, e.target.value)
              }
              onClick={(e) => e.stopPropagation()}
              placeholder={question.placeholder}
              className={fieldBase}
            />
          </div>
        );

      case "dropdown":
        return (
          <div key={question.id} className="space-y-2">
            <div>{label}</div>
            <select
              value={currentAnswer || ""}
              onChange={(e) =>
                handleDynamicAnswer(testId, question.id, e.target.value)
              }
              onClick={(e) => e.stopPropagation()}
              className={fieldBase}
              required={question.is_required}
            >
              <option value="">Select an option</option>
              {question.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      default:
        return null;
    }
  };

  // Check if all required dynamic questions are answered for a test
  const isTestReadyForSubmission = (test) => {
    const testQuestions = getTestQuestions(test.id);
    if (!testQuestions || testQuestions.length === 0) {
      return true;
    }

    const testAnswers = dynamicAnswers[test.id] || {};

    return testQuestions.every((question) => {
      if (!question.is_required) return true;

      const answer = testAnswers[question.id];
      if (question.field_type === "checkbox") {
        return answer && answer.length > 0;
      }
      return answer && answer.toString().trim() !== "";
    });
  };

  // Check if all selected tests are ready for submission
  const areAllTestsReady = () => {
    return selectedTests.every((test) => isTestReadyForSubmission(test));
  };

  const handleSubmitOrder = async () => {
    if (selectedTests.length === 0) {
      toast.error("Please select at least one test");
      return;
    }
    if (!areAllTestsReady()) {
      toast.error("Please answer all required questions for selected tests");
      return;
    }

    try {
      // 1) Create a visit first (backend will give us visit.id)
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const localDate = `${yyyy}-${mm}-${dd}`;
      const visitPayload = {
        patient_id: currentPatient.id,
        receptionist_id: user?.id || null,
        visit_date: localDate,
        visit_time: now.toTimeString().slice(0, 5), // HH:MM local time
        status: "registered",
        priority: "routine",
      };
      const visit = await dispatch(createVisit(visitPayload)).unwrap();

      // 2) Create test orders using visit_id
      const orderPromises = selectedTests.map((test) =>
        dispatch(
          createTestOrder({
            visit_id: visit.data.id,
            test_id: test.id,
            priority: "normal",
            payment_status: paymentStatus,
            sample_type: test.sample_type || "serum",
            dynamic_answers: dynamicAnswers[test.id] || {},
          })
        ).unwrap()
      );

      await Promise.all(orderPromises);

      toast.success("Visit and test orders created successfully!");
      if (onOrderComplete) onOrderComplete();
    } catch (error) {
      console.error("Failed to create visit/test orders:", error);
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to create visit or test orders"
      );
    }
  };

  const isLoading = orderLoading || testsLoading;

  const hasSelection = selectedTests.length > 0;
  const total = calculateTotal();
  const missingRequired = hasSelection && !areAllTestsReady();
  const showMobileBar = hasSelection;

  return (
    <div className={"pt-6 " + (showMobileBar ? "pb-24" : "")}
    >
      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center rounded-full bg-[#36F1A2]/15 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#36F1A2]/40">
                Step 2 of 2
              </div>
              <h2 className="mt-2 text-xl font-semibold text-gray-900">Order laboratory tests</h2>
              <p className="mt-1 text-sm text-gray-600">
                Select tests, review totals, and complete any required questions.
              </p>
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-2xl bg-white/70 px-4 py-3 ring-1 ring-gray-200 backdrop-blur">
              <span className="inline-flex items-center rounded-full bg-[#235F72] px-3 py-1 text-xs font-semibold text-white">
                Patient
              </span>
              <div className="min-w-0 truncate text-sm font-semibold text-gray-900">
                {currentPatient?.full_name || patient?.full_name || "—"}
              </div>
              <div className="flex-none text-xs text-gray-600">
                Card: {currentPatient?.card_number || patient?.card_number || "—"}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-6 sm:px-6">
          <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <main className="min-w-0 space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                </div>

                <div className="relative w-full sm:max-w-md">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    <Search className="h-4 w-4" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search tests by name or description…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl bg-white py-3 pl-10 pr-10 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200/70">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Categories</div>
                  <div className="text-xs text-gray-600">Tap to filter</div>
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {categoryChips.map((category) => {
                    const isActive =
                      selectedCategory === category.id ||
                      (category.id !== "all" && selectedCategory === Number(category.id));

                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() =>
                          setSelectedCategory(
                            category.id === "all" ? "all" : Number(category.id)
                          )
                        }
                        className={
                          "whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] " +
                          (isActive
                            ? "bg-[#36F1A2]/15 text-[#235F72] ring-1 ring-[#36F1A2]/50"
                            : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50")
                        }
                      >
                        {category.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <section className="min-w-0 rounded-2xl bg-white ring-1 ring-gray-200/70">
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900">Available tests</div>
                  <div className="text-xs text-gray-600">
                    {filteredTests.length} shown
                  </div>
                </div>

                <div className="max-h-[520px] space-y-3 overflow-y-auto p-4 sm:max-h-[620px]">
                  {testsLoading ? (
                    <div className="flex items-center justify-center rounded-2xl bg-gray-50 p-10 text-sm text-gray-700">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin text-[#36F1A2]" />
                      Loading tests…
                    </div>
                  ) : filteredTests.length === 0 ? (
                    <div className="rounded-2xl bg-gray-50 p-8 text-center text-sm text-gray-700 ring-1 ring-gray-200">
                      No tests found matching your criteria.
                    </div>
                  ) : (
                    filteredTests.map((test) => {
                      const isSelected = selectedTests.find((t) => t.id === test.id);
                      const testQuestions = getTestQuestions(test.id);
                      const hasDynamicQuestions = testQuestions && testQuestions.length > 0;
                      const isReady = isTestReadyForSubmission(test);
                      const isLoadingQuestions = loadingQuestionsForTest === test.id;
                      const isExpanded = Boolean(expandedQuestionsByTestId[test.id]);

                      return (
                        <div
                          key={test.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => (isLoading ? null : handleTestToggle(test))}
                          onKeyDown={(e) => {
                            if (isLoading) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleTestToggle(test);
                            }
                          }}
                          className={
                            "group rounded-2xl bg-white p-4 shadow-sm ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] hover:shadow-md " +
                            (isSelected
                              ? "ring-[#36F1A2]/60 bg-[#36F1A2]/5 border-l-4 border-[#36F1A2]"
                              : "ring-gray-200 border-l-4 border-transparent")
                          }
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="text-sm font-semibold text-gray-900">
                                  {test.name}
                                </div>
                                {hasDynamicQuestions ? (
                                  <span className="inline-flex items-center rounded-full bg-[#085DB6]/10 px-2.5 py-1 text-xs font-semibold text-[#085DB6] ring-1 ring-[#085DB6]/20">
                                    Questions
                                  </span>
                                ) : null}
                                {isSelected && hasDynamicQuestions ? (
                                  isReady ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                      Ready
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                                      Required answers missing
                                    </span>
                                  )
                                ) : null}
                                {isLoadingQuestions ? (
                                  <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    Loading questions…
                                  </span>
                                ) : null}
                              </div>

                              {test.description ? (
                                <div className="mt-1 line-clamp-2 text-sm text-gray-600">
                                  {test.description}
                                </div>
                              ) : null}

                              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                                {test.sample_type ? <span>Sample: {test.sample_type}</span> : null}
                                {test.processing_time ? (
                                  <span>Time: {test.processing_time}</span>
                                ) : null}
                                {test.tube_type ? <span>Tube: {test.tube_type}</span> : null}
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-2">
                              <div className="text-right">
                                <div className="text-lg font-semibold text-[#235F72]">
                                  ETB {test.price || 0}
                                </div>
                                <div className="text-xs text-gray-600">{test.category_name}</div>
                              </div>

                              <div
                                className={
                                  "inline-flex h-8 w-8 items-center justify-center rounded-xl ring-1 transition " +
                                  (isSelected
                                    ? "bg-[#36F1A2] text-white ring-[#36F1A2]"
                                    : "bg-gray-50 text-gray-600 ring-gray-200 group-hover:bg-white")
                                }
                                aria-hidden="true"
                              >
                                {isSelected ? (
                                  <CheckCircle2 className="h-4 w-4" />
                                ) : (
                                  <span className="text-xs font-bold">+</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {isSelected && hasDynamicQuestions ? (
                            <div
                              className="mt-4 rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="text-sm font-semibold text-gray-900">Dynamic questions</div>
                                  <div className="mt-0.5 text-xs text-gray-600">
                                    Answer required items to enable submission.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedQuestionsByTestId((prev) => ({
                                      ...prev,
                                      [test.id]: !Boolean(prev[test.id]),
                                    }));
                                  }}
                                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp className="h-4 w-4" />
                                      Collapse
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-4 w-4" />
                                      Expand
                                    </>
                                  )}
                                </button>
                              </div>

                              {missingRequired && !isReady ? (
                                <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
                                  Required fields are missing for this test.
                                </div>
                              ) : null}

                              {isExpanded ? (
                                <div className="mt-4 space-y-4">
                                  {testQuestions.map((question) => (
                                    <div key={question.id} className="space-y-1">
                                      {renderDynamicQuestion(test.id, question)}
                                      {question.is_required &&
                                      !dynamicAnswers[test.id]?.[question.id] ? (
                                        <div className="text-xs font-medium text-red-600">
                                          This field is required.
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {isSelected && isLoadingQuestions ? (
                            <div className="mt-4 rounded-2xl bg-gray-50 p-4 text-sm text-gray-700 ring-1 ring-gray-200">
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin text-[#36F1A2]" />
                                Loading questions…
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            </main>

            <aside className="hidden min-w-0 lg:block">
              <div className="sticky top-6 space-y-4">
                <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200/70">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Order summary</div>
                      <div className="mt-0.5 text-xs text-gray-600">
                        {selectedTests.length} test(s) selected
                      </div>
                    </div>
                    {missingRequired ? (
                      <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                        Required missing
                      </span>
                    ) : null}
                  </div>

                  {!hasSelection ? (
                    <div className="mt-4 rounded-2xl bg-gray-50 p-4 text-sm text-gray-700 ring-1 ring-gray-200">
                      Select one or more tests to see totals here.
                    </div>
                  ) : (
                    <div className="mt-4 space-y-3">
                      <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                        {selectedTests.map((test) => {
                          const testQuestions = getTestQuestions(test.id);
                          const hasQuestions = testQuestions && testQuestions.length > 0;
                          const isReady = isTestReadyForSubmission(test);

                          return (
                            <div
                              key={test.id}
                              className="rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200/70"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {test.name}
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold text-[#235F72]">
                                      ETB {test.price || 0}
                                    </span>
                                    {hasQuestions ? (
                                      <span
                                        className={
                                          "rounded-full px-2 py-0.5 text-xs font-semibold ring-1 " +
                                          (isReady
                                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                            : "bg-amber-50 text-amber-700 ring-amber-200")
                                        }
                                      >
                                        {isReady ? "Ready" : "Incomplete"}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTestToggle(test);
                                  }}
                                  className="inline-flex items-center justify-center rounded-xl bg-white p-2 text-red-600 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                                  aria-label={`Remove ${test.name}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-200">
                        <div className="mb-4">
                          <label className="mb-1 block text-xs font-semibold text-gray-700">
                            Payment status
                          </label>
                          <select
                            value={paymentStatus}
                            onChange={(e) => setPaymentStatus(e.target.value)}
                            className="w-full rounded-xl bg-white px-3 py-2 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                          >
                            <option value="unpaid">Unpaid</option>
                            <option value="partial">Partial</option>
                            <option value="paid">Paid</option>
                            <option value="refunded">Refunded</option>
                          </select>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">Total</span>
                          <span className="text-xl font-bold text-[#235F72]">ETB {total}</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleSubmitOrder}
                          disabled={isLoading || selectedTests.length === 0 || !areAllTestsReady()}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {isLoading ? "Creating orders…" : "Create test orders"}
                        </button>

                        <button
                          type="button"
                          className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-white px-4 py-3 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {showMobileBar ? (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur ring-1 ring-gray-200 lg:hidden">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
            <div className="min-w-0">
              <div className="text-xs text-gray-600">Selected</div>
              <div className="text-sm font-semibold text-gray-900">
                {selectedTests.length} test(s) • <span className="text-[#235F72]">ETB {total}</span>
              </div>
              <div className="mt-0.5 text-xs text-gray-600">
                Payment: {paymentStatus}
              </div>
              {missingRequired ? (
                <div className="mt-0.5 text-xs font-semibold text-amber-700">
                  Required answers missing
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={handleSubmitOrder}
              disabled={isLoading || selectedTests.length === 0 || !areAllTestsReady()}
              className="inline-flex flex-none items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isLoading ? "Saving…" : "Create"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TestOrder;
